export const disease2Module = {
  id: "disease2",
  title: "Thyroid Diseases: Part 2",
  sections: [
    {
      type: "text",
      title: "Thyroid Nodule Evaluation",
      content: `
        <p>Thyroid nodules are extremely common — present in up to 50% of adults on ultrasound. The vast majority (~95%) are benign. The clinical challenge is identifying the small proportion that are malignant.</p>

        <div class="highlight-box">
          <p><strong>Risk factors for malignancy in a thyroid nodule</strong></p>
          <ul>
            <li>Male sex</li>
            <li>Age &lt;20 or &gt;70 years</li>
            <li>History of <span class="purple-term">neck irradiation</span> (especially in childhood)</li>
            <li>Family history of thyroid cancer or MEN2 / FAP</li>
            <li>Rapid growth, hard consistency, fixed to surrounding structures</li>
            <li>Cervical lymphadenopathy</li>
            <li>Hoarseness (recurrent laryngeal nerve involvement)</li>
            <li>Dysphagia, stridor</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Step-by-step approach to a thyroid nodule</strong></p>
          <ol>
            <li><strong>TSH</strong>: if suppressed → functional (hot) nodule likely → scintigraphy (hot = almost certainly benign, no FNAC needed)</li>
            <li><strong>Ultrasound</strong>: characterise nodule — TIRADS or UK BTA guidelines
              <ul>
                <li>Suspicious features: hypoechoic, irregular/spiculated margins, microcalcifications, taller-than-wide orientation, central vascularity</li>
                <li>Benign features: purely cystic, spongiform, colloid cyst with comet-tail artefact</li>
              </ul>
            </li>
            <li><strong>FNAC</strong> (fine needle aspiration cytology): key investigation for solid/suspicious nodules ≥1 cm
              <ul>
                <li>Reported using the <span class="purple-term">Bethesda system</span>: I = non-diagnostic, II = benign (80% risk <1%), III = atypia of uncertain significance, IV = follicular neoplasm, V = suspicious for malignancy, VI = malignant</li>
              </ul>
            </li>
            <li>Bethesda IV ("follicular neoplasm") → diagnostic hemithyroidectomy (FNAC cannot distinguish follicular adenoma from follicular carcinoma)</li>
          </ol>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Hot vs Cold Nodule</div>
          <div class="mnemonic-content"><strong>Hot nodule</strong> (high RAI uptake) = autonomous function = almost always benign<br/><strong>Cold nodule</strong> (low RAI uptake) = not functioning = 5–15% risk of malignancy → needs FNAC</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 45-year-old woman with a 2 cm thyroid nodule has a suppressed TSH. What is the next most appropriate investigation?",
      options: [
        "FNAC immediately — all nodules ≥1 cm require cytology",
        "Scintigraphy (RAI uptake scan) — a suppressed TSH suggests a functioning (hot) nodule, which is almost always benign and does not need FNAC",
        "CT neck — to assess for malignant invasion",
        "Total thyroidectomy — any nodule with a suppressed TSH needs removal",
      ],
      correct: 1,
      explanation:
        "A suppressed TSH in the presence of a thyroid nodule suggests the nodule is autonomously functioning (a 'hot' nodule) — producing thyroid hormone independently of TSH. Hot nodules have a <1% risk of malignancy and do not require FNAC. Scintigraphy confirms the hot nodule (focal increased uptake with suppression of surrounding tissue). The appropriate management is to treat the hyperthyroidism (RAI or surgery), not to biopsy.",
    },
    {
      type: "question",
      text: "FNAC of a thyroid nodule returns as Bethesda Category IV (follicular neoplasm). What does this mean and what is the next step?",
      options: [
        "The nodule is definitively malignant — proceed to total thyroidectomy",
        "FNAC cannot distinguish follicular adenoma from follicular carcinoma — diagnostic hemithyroidectomy with histological examination of the capsule is required",
        "The nodule is benign — observe with repeat ultrasound in 12 months",
        "Repeat FNAC immediately — Bethesda IV is a non-diagnostic result",
      ],
      correct: 1,
      explanation:
        "Bethesda Category IV ('follicular neoplasm') indicates cells that appear follicular on cytology but cannot be classified as benign or malignant by FNAC alone. The distinction between follicular adenoma (benign) and follicular carcinoma (malignant) requires histological assessment of the capsule and vessels — which requires the entire excised nodule. Therefore, diagnostic hemithyroidectomy is performed. If histology confirms follicular carcinoma (capsular/vascular invasion), completion thyroidectomy is usually performed.",
    },
    {
      type: "text",
      title: "Papillary Thyroid Carcinoma (PTC)",
      content: `
        <p><span class="glossary-term">Papillary thyroid carcinoma (PTC)</span> is the most common thyroid malignancy, accounting for ~80–85% of cases. Despite frequent lymph node spread, the prognosis is generally excellent.</p>

        <div class="highlight-box">
          <p><strong>Aetiology & molecular biology</strong></p>
          <ul>
            <li>Most common risk factor: <strong>childhood radiation exposure</strong> (previous neck irradiation, nuclear accidents — Chernobyl data)</li>
            <li><strong>BRAF V600E mutation</strong> (~50% of PTCs) — most common somatic mutation; associated with more aggressive behaviour and radioiodine resistance</li>
            <li><strong>RET/PTC rearrangement</strong> — especially radiation-induced PTCs</li>
            <li>RAS mutation — follicular variant of PTC</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Histological hallmarks</strong></p>
          <ul>
            <li><span class="purple-term">"Orphan Annie" nuclei</span> — large, pale, empty-looking nuclei with central clearing (nuclear grooves and pseudo-inclusions)</li>
            <li><span class="purple-term">Psammoma bodies</span> — laminated calcified concentric rings (in ~50%)</li>
            <li>Papillary architecture (fibrovascular cores lined by tumour cells)</li>
            <li>Variants: classic papillary, follicular variant (encapsulated = good prognosis), tall cell (aggressive, BRAF+), diffuse sclerosing</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Spread & prognosis</strong></p>
          <ul>
            <li>Predominantly <strong>lymphatic spread</strong> to cervical lymph nodes</li>
            <li>Lymph node metastases are common but <strong>do NOT significantly worsen prognosis</strong> in PTC</li>
            <li>Distant metastases (lung, bone) are uncommon but do worsen prognosis</li>
            <li>Excellent overall prognosis — &gt;95% 10-year survival for low-risk PTC</li>
            <li>Poor prognostic factors: age &gt;55, tumour &gt;4 cm, extrathyroidal extension, distant metastases, BRAF V600E+, tall cell variant</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Treatment</strong></p>
          <ul>
            <li>Total thyroidectomy (or hemithyroidectomy for low-risk, unifocal &lt;1 cm)</li>
            <li><span class="purple-term">TSH suppression</span> with levothyroxine (target TSH 0.1–0.5 mIU/L for high-risk; &lt;2.5 for low-risk)</li>
            <li>Radioactive iodine (I-131) ablation for high-risk PTC (takes up iodine — derived from follicular cells)</li>
            <li>Thyroglobulin (Tg) monitoring — tumour marker; if Tg rises after total thyroidectomy, suspect recurrence</li>
            <li>BRAF inhibitors (dabrafenib + trametinib) for advanced/refractory BRAF V600E+ disease</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A pathologist describes thyroid cancer tissue showing 'Orphan Annie' nuclei with nuclear grooves and psammoma bodies. What is the diagnosis?",
      options: [
        "Follicular thyroid carcinoma — follicular architecture with capsular invasion",
        "Papillary thyroid carcinoma — nuclear features are pathognomonic",
        "Medullary thyroid carcinoma — amyloid stroma and calcitonin positive",
        "Anaplastic carcinoma — pleomorphic giant cells",
      ],
      correct: 1,
      explanation:
        "'Orphan Annie' nuclei (large, pale, empty-appearing nuclei with grooves and pseudo-inclusions) and psammoma bodies (concentric laminated calcifications) are the pathognomonic histological features of papillary thyroid carcinoma. These nuclear features alone are sufficient to diagnose PTC even in a lymph node metastasis. Note: despite being called 'papillary', some variants (e.g., follicular variant of PTC) do not show papillary architecture but still have these nuclear features.",
    },
    {
      type: "question",
      text: "After total thyroidectomy and RAI ablation for papillary thyroid carcinoma, which tumour marker is used to monitor for recurrence?",
      options: [
        "Calcitonin — produced by follicular cells",
        "CEA — elevated in all thyroid cancers",
        "Thyroglobulin (Tg) — produced by thyroid follicular cells; should be undetectable after complete ablation",
        "TSH — elevated TSH suggests recurrence",
      ],
      correct: 2,
      explanation:
        "Thyroglobulin (Tg) is produced exclusively by thyroid follicular cells (normal and malignant). After total thyroidectomy and RAI ablation, Tg should be undetectable. A rising Tg level during follow-up indicates recurrent or metastatic PTC. Tg is measured alongside anti-Tg antibodies (which can interfere with the assay). Stimulated Tg (after TSH stimulation with recombinant TSH or thyroid hormone withdrawal) is more sensitive for detecting low-volume disease.",
    },
    {
      type: "text",
      title: "Follicular Thyroid Carcinoma (FTC)",
      content: `
        <p><span class="glossary-term">Follicular thyroid carcinoma (FTC)</span> is the second most common thyroid malignancy (~10–15%). It is distinct from PTC in its spread pattern, molecular biology, and inability to diagnose on FNAC.</p>

        <div class="highlight-box">
          <p><strong>Key distinguishing features from PTC</strong></p>
          <ul>
            <li><strong>FNAC cannot diagnose FTC</strong> — follicular cells appear the same in adenoma and carcinoma; histological examination of the entire excised nodule is required</li>
            <li>Diagnosis requires: <span class="purple-term">capsular invasion</span> AND/OR <span class="purple-term">vascular invasion</span> on histology</li>
            <li>Presents as Bethesda IV on FNAC → diagnostic hemithyroidectomy</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Spread & prognosis</strong></p>
          <ul>
            <li><strong>Haematogenous spread</strong> (NOT lymphatic) — to lung ("cannonball" metastases) and bone (lytic lesions)</li>
            <li>Bone mets: may be the presenting feature — a lytic bone lesion with a thyroid mass should raise suspicion</li>
            <li>Lymph node spread is rare (in contrast to PTC)</li>
            <li>Prognosis: minimally invasive FTC = excellent (similar to PTC); widely invasive = poor</li>
            <li>Molecular: <strong>RAS mutation</strong> (most common), PAX8-PPARγ fusion</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Treatment</strong></p>
          <ul>
            <li>Total thyroidectomy (after diagnostic hemithyroidectomy confirms malignancy)</li>
            <li>RAI ablation — FTC takes up radioiodine (follicular cell-derived)</li>
            <li>TSH suppression with levothyroxine</li>
            <li>Thyroglobulin monitoring for recurrence</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">PTC vs FTC Spread</div>
          <div class="mnemonic-content"><strong>PTC</strong> — Lymphatic spread → Cervical nodes (Lymph nodes = L = Lymphatic = PTC)<br/><strong>FTC</strong> — Haematogenous spread → Lung and Bone (Far away = F = FTC = Far spread)</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 52-year-old woman undergoes hemithyroidectomy for a Bethesda IV nodule. Histology shows a follicular-patterned tumour with vascular invasion through the capsule. What is the diagnosis and what is the next step?",
      options: [
        "Follicular adenoma — vascular invasion does not distinguish benign from malignant",
        "Follicular thyroid carcinoma — completion thyroidectomy followed by RAI ablation",
        "Papillary thyroid carcinoma follicular variant — BRAF testing and neck dissection",
        "Hürthle cell carcinoma — completion thyroidectomy but RAI is not useful",
      ],
      correct: 1,
      explanation:
        "Vascular invasion through the capsule is diagnostic of follicular thyroid carcinoma (FTC). Follicular adenoma is benign — it may show capsular thickening but must NOT invade the capsule or vessels. Confirmation of FTC on hemithyroidectomy histology warrants completion total thyroidectomy (to remove remaining thyroid tissue for RAI ablation) followed by radioactive iodine ablation and levothyroxine for TSH suppression and thyroglobulin monitoring.",
    },
    {
      type: "text",
      title: "Medullary Thyroid Carcinoma (MTC)",
      content: `
        <p><span class="glossary-term">Medullary thyroid carcinoma (MTC)</span> arises from <strong>parafollicular C cells</strong> and produces <strong>calcitonin</strong>. It accounts for ~5% of thyroid cancers but is clinically important due to its hereditary association with MEN2.</p>

        <div class="highlight-box">
          <p><strong>Hereditary vs sporadic</strong></p>
          <ul>
            <li>~75% sporadic — somatic RET mutation; older patients; usually unilateral</li>
            <li>~25% hereditary — germline RET mutation; <strong>bilateral and multifocal</strong> (C-cell hyperplasia → MTC); diagnosed earlier</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>MEN2 associations — RET proto-oncogene mutations</strong></p>
          <ul>
            <li><strong>MEN2A (Sipple's syndrome)</strong>: MTC + <span class="purple-term">phaeochromocytoma</span> + primary hyperparathyroidism (multigland); RET exon 10/11 mutations</li>
            <li><strong>MEN2B</strong>: MTC (most aggressive) + phaeochromocytoma + <span class="purple-term">mucosal neuromas</span> (lips, tongue, eyelids) + Marfanoid habitus (no hyperparathyroidism); RET codon 918 mutation</li>
            <li><strong>Familial MTC (FMTC)</strong>: MTC only; RET mutations; less aggressive</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Diagnosis & markers</strong></p>
          <ul>
            <li><strong>Calcitonin</strong>: tumour marker; elevated preoperatively; monitored postoperatively (rising = recurrence); also useful for family screening</li>
            <li><strong>CEA</strong>: correlates with disease progression and tumour burden</li>
            <li>Histology: sheets of cells in amyloid stroma (calcitonin deposits as amyloid — <span class="purple-term">Congo red positive</span>)</li>
            <li>Does <strong>NOT</strong> take up radioiodine (C cells do not trap iodide)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Treatment</strong></p>
          <ul>
            <li><strong>Total thyroidectomy + central neck dissection</strong> (even for small tumours — bilateral, multifocal)</li>
            <li>RAI is NOT used (C cells do not express NIS)</li>
            <li>Exclude phaeochromocytoma BEFORE thyroid surgery (risk of hypertensive crisis intraoperatively)</li>
            <li>Metastatic/progressive: <span class="purple-term">vandetanib</span> or <span class="purple-term">cabozantinib</span> (RET + VEGFR kinase inhibitors)</li>
            <li>RET-specific inhibitors: selpercatinib, pralsetinib (for RET-mutant MTC)</li>
            <li>Screen all relatives: genetic testing for RET → prophylactic thyroidectomy in mutation carriers</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with MTC is found to have a RET germline mutation. Before proceeding to thyroidectomy, what must be excluded and why?",
      options: [
        "Hyperparathyroidism — hypercalcaemia can cause arrhythmias during thyroid surgery",
        "Phaeochromocytoma — if present and unrecognised, anaesthesia and surgical stress can trigger a life-threatening hypertensive crisis",
        "Cushing's syndrome — cortisol excess impairs wound healing",
        "Graves' disease — concurrent hyperthyroidism must be treated first",
      ],
      correct: 1,
      explanation:
        "In MEN2A and MEN2B, phaeochromocytoma coexists with MTC. Phaeochromocytomas secrete catecholamines (adrenaline, noradrenaline), and unrecognised phaeochromocytoma can cause a life-threatening hypertensive crisis when catecholamines are released during surgery (from handling, intubation, or stress). The rule is: 'Phaeo before Thyroid' — always screen for (and treat) phaeochromocytoma before thyroidectomy. Screening: plasma or urinary metanephrines + imaging.",
    },
    {
      type: "question",
      text: "Which thyroid cancer does NOT benefit from radioactive iodine treatment and why?",
      options: [
        "Papillary thyroid carcinoma — it is too well-differentiated to take up iodine",
        "Medullary thyroid carcinoma — it arises from C cells, which do not express the sodium-iodide symporter (NIS)",
        "Follicular thyroid carcinoma — it spreads haematogenously and iodine cannot reach bone metastases",
        "Anaplastic carcinoma — it takes up iodine but is too rapidly fatal for RAI to be effective",
      ],
      correct: 1,
      explanation:
        "Medullary thyroid carcinoma arises from parafollicular C cells (not follicular cells). C cells do not express the sodium-iodide symporter (NIS) — the transporter responsible for iodine uptake in follicular cells. Therefore, MTC does not take up radioactive iodine, and RAI ablation is completely ineffective. Treatment for metastatic MTC relies on RET kinase inhibitors (vandetanib, cabozantinib, selpercatinib). Both PTC and FTC (follicular cell-derived) retain NIS expression and benefit from RAI.",
    },
    {
      type: "text",
      title: "Anaplastic Thyroid Carcinoma & MEN Syndromes",
      content: `
        <p><span class="glossary-term">Anaplastic thyroid carcinoma (ATC)</span> is the rarest (&lt;2%) but most lethal thyroid cancer. It is an endocrine emergency.</p>

        <div class="highlight-box">
          <p><strong>Anaplastic thyroid carcinoma</strong></p>
          <ul>
            <li>Predominantly elderly patients (&gt;65 years); may arise from dedifferentiation of PTC or FTC</li>
            <li><strong>Median survival: 3–6 months</strong>; &lt;10% survive 5 years</li>
            <li>Features: rapidly enlarging hard thyroid mass; dysphagia, dyspnoea, stridor, hoarseness (RLN invasion); invades surrounding structures</li>
            <li>Histology: undifferentiated pleomorphic giant cells and spindle cells; no follicular architecture; high mitotic rate</li>
            <li>Does NOT take up RAI; TSH suppression ineffective</li>
            <li>BRAF V600E mutation in ~45%</li>
            <li>Treatment: surgery rarely achievable; <strong>dabrafenib + trametinib</strong> (BRAF + MEK inhibitors) if BRAF V600E+; external beam radiotherapy + chemotherapy (doxorubicin); mostly palliative</li>
          </ul>
        </div>

        <hr/>

        <p><strong>Multiple Endocrine Neoplasia (MEN) — summary including thyroid</strong></p>

        <div class="highlight-box">
          <p><strong>MEN1 (Wermer's syndrome)</strong> — MEN1 gene (menin tumour suppressor, chromosome 11q13)</p>
          <ul>
            <li><strong>"3 Ps"</strong>: Parathyroid (HPT — multigland, first manifestation) + Pancreatic neuroendocrine tumours (insulinoma, gastrinoma/Zollinger-Ellison, VIPoma, glucagonoma) + Pituitary (prolactinoma most common, GH → acromegaly)</li>
            <li>No thyroid component</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>MEN2A (Sipple's syndrome)</strong> — RET mutation (exon 10/11)</p>
          <ul>
            <li>MTC (earliest and most penetrant) + Phaeochromocytoma (~50%) + Primary HPT (~20%, single/multigland)</li>
            <li>Treat phaeochromocytoma before MTC</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>MEN2B</strong> — RET codon 918 mutation (de novo in 50%)</p>
          <ul>
            <li>Most aggressive MTC (earliest onset, worst prognosis) + Phaeochromocytoma + <span class="purple-term">Mucosal neuromas</span> (lips, tongue, eyelids) + Marfanoid habitus + Intestinal ganglioneuromas</li>
            <li><strong>No hyperparathyroidism</strong> (distinguishes from MEN2A)</li>
            <li>Prophylactic thyroidectomy within first 6 months of life in RET codon 918 carriers</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">MEN Summary</div>
          <div class="mnemonic-content"><strong>MEN1</strong> = "3 Ps" — Parathyroid + Pancreas + Pituitary (no thyroid)<br/><strong>MEN2A</strong> = MTC + Phaeo + Parathyroid (HPT)<br/><strong>MEN2B</strong> = MTC + Phaeo + Mucosal neuromas + Marfanoid (no HPT)</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 22-year-old man presents with a 3 cm thyroid mass, thick lips, and bumpy lesions on the tongue. His mother died of thyroid cancer at age 30. What is the most likely diagnosis?",
      options: [
        "MEN1 — pancreatic insulinoma with ectopic tumour on tongue",
        "MEN2B — mucosal neuromas + Marfanoid habitus + medullary thyroid carcinoma",
        "MEN2A — primary hyperparathyroidism presenting with tongue deposits",
        "Sporadic papillary thyroid carcinoma with lymph node metastases to the tongue",
      ],
      correct: 1,
      explanation:
        "MEN2B is characterised by medullary thyroid carcinoma (earliest onset, most aggressive of all MEN-associated MTCs), phaeochromocytoma, and mucosal neuromas — bumpy neuromas on the lips, tongue, and eyelids — plus a Marfanoid body habitus. Unlike MEN2A, there is no hyperparathyroidism. The family history of early thyroid cancer death strongly suggests hereditary MTC. RET codon 918 mutation is the diagnostic finding. Prophylactic thyroidectomy should be performed within 6 months of birth in confirmed mutation carriers.",
    },
    {
      type: "question",
      text: "A 70-year-old woman presents with rapidly progressive difficulty breathing and swallowing from a newly diagnosed fixed thyroid mass. Biopsy shows pleomorphic giant cells. What is the diagnosis and what targeted therapy is available if BRAF testing is positive?",
      options: [
        "Medullary thyroid carcinoma — treat with vandetanib (RET inhibitor)",
        "Anaplastic thyroid carcinoma — dabrafenib + trametinib if BRAF V600E positive",
        "Papillary thyroid carcinoma — total thyroidectomy + RAI",
        "Lymphoma — R-CHOP chemotherapy",
      ],
      correct: 1,
      explanation:
        "Anaplastic thyroid carcinoma is characterised by rapid growth, local invasion, and pleomorphic undifferentiated histology. It carries a median survival of 3–6 months. BRAF V600E is present in ~45% of ATCs, and the combination of dabrafenib (BRAF inhibitor) + trametinib (MEK inhibitor) has shown significant responses in BRAF V600E+ ATC, transforming a previously universally fatal diagnosis into one with at least some treatment options. External beam radiotherapy and chemotherapy are used for BRAF wild-type disease.",
    },
    {
      type: "question",
      text: "A patient with MEN1 has peptic ulcer disease resistant to proton pump inhibitors, diarrhoea, and very high fasting gastrin levels. What is the pancreatic tumour causing this?",
      options: [
        "Insulinoma — causes hypoglycaemia and low fasting glucose",
        "Gastrinoma — causes Zollinger-Ellison syndrome: hypergastrinaemia, peptic ulcers, diarrhoea",
        "VIPoma — causes watery diarrhoea, hypokalaemia, and achlorhydria (WDHA syndrome)",
        "Glucagonoma — causes necrolytic migratory erythema and hyperglycaemia",
      ],
      correct: 1,
      explanation:
        "In MEN1, gastrinoma (a gastrin-secreting tumour of the pancreas or duodenum) causes Zollinger-Ellison syndrome — massively elevated gastrin → hyperstimulation of parietal cells → high acid output → severe, refractory peptic ulcers and diarrhoea (from acid inactivating pancreatic enzymes). The fasting gastrin level is markedly elevated (>10× normal). Diagnosis: secretin stimulation test (paradoxical rise in gastrin). Gastrinoma is the most common functional pancreatic NET in MEN1.",
    },
  ],
};
